import LayoutSearch from "../components/LayoutSearch";
import css from "../css/agents/layout.module.css";

import Breadcrumbs from "../components/agents/Breadcrumbs";
import Filters from "../components/agents/Filters";
import ActiveFilters from "../components/agents/ActiveFilters";
import AgentList from "../components/agents/AgentList";
import Paginator from "../components/common/Paginator";

import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import Error from "../pages/_error";
import {
    getApiQueryFromFilters,
    getFiltersFromQuery,
    getQueryFromFilters,
} from "../components/agents/FilterValue";
import axiosResiInstance from "../http/axiosResi";
import reportFailedRequest from "../service/error";
import { getSeoAgents } from "../service/seo";
import { sendAgentView } from "../service/agent-view";
import queryString from "query-string";
import useMobile from "../hooks/useMobile";
import useHasMounted from "../hooks/useHasMounted";
import debounce from "lodash.debounce";

const PAGE_SIZE = 10;
const SEARCH_LIMITATION = 10000; // 10000 agents

export const getAgents = (filters, page, pageSize = PAGE_SIZE) => {
    const queryFilters = getApiQueryFromFilters(filters);
    const offset = (page - 1) * pageSize;

    return axiosResiInstance
        .get("/agents", {
            params: {
                ...queryFilters,
                format: "featured_first_last",
                limit: pageSize,
                offset,
            },
        })
        .then((response) => {
            const agentIds = response.data.data.map((agent) => agent.id);

            if (agentIds.length) {
                sendAgentView(agentIds, "FIND_AGENT_SEARCH_RESULTS");
            }

            return response;
        });
};

const getHref = (filters, page, isHref = false) => {
    const location = filters.location ? filters.location.value : null;

    let query = getQueryFromFilters(filters);
    query["page"] = page;

    if (isHref) {
        return {
            pathname: "/agents",
            query: {
                ...query,
                location: location ? location.url : null,
            },
        };
    }

    let url = "/real_estate_agents";

    if (location) {
        url += `/${location.url}`;
    }

    return {
        pathname: url,
        query,
    };
};

export const getPages = (total, pageSize = PAGE_SIZE) => {
    const pages = Math.ceil(total / pageSize);
    const maxPage = Math.floor(SEARCH_LIMITATION / pageSize);

    if (pages > maxPage) {
        return maxPage;
    }

    return pages;
};

const getPage = (page) => {
    const maxPage = Math.floor(SEARCH_LIMITATION / PAGE_SIZE);

    if (page > maxPage) {
        return maxPage;
    }

    return page;
};

function reducer(state, action) {
    switch (action.type) {
        case "loaded_agents": {
            return {
                ...state,
                agents: action.agents,
                agentsTotal: action.agentsTotal,
            };
        }
        case "toggled_filters": {
            return {
                ...state,
                filtersOpen: !state.filtersOpen,
            };
        }
        case "hid_filters": {
            return {
                ...state,
                filtersOpen: false,
            };
        }
        case "showed_filters": {
            return {
                ...state,
                filtersOpen: true,
            };
        }
        case "changed_filter": {
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [action.name]: action.value,
                },
                page: 1,
            };
        }
        case "changed_page": {
            return {
                ...state,
                page: action.page,
            };
        }
        case "cleared_filter": {
            let filters = { ...state.filters };
            const { name, label } = action;

            const labels = filters[name].label.split(",");
            let newFilterLabels = [];
            let newFilterValues = [];

            labels.forEach((filterLabel, index) => {
                if (filterLabel.trim() !== label.trim()) {
                    newFilterLabels.push(labels[index]);
                    newFilterValues.push(filters[name].value[index]);
                }
            });

            if (newFilterLabels.length === 0) {
                filters[name].label = "";
                filters[name].value = null;
            } else {
                filters[name].label = newFilterLabels.join(",");
                filters[name].value = newFilterValues;
            }

            return {
                ...state,
                filters,
                page: 1,
            };
        }
        case "cleared_all_filters": {
            let filters = { ...state.filters };

            Object.entries(filters).map((filter) => {
                if (filter[0] === "location") {
                    return;
                }

                filters[filter[0]].value = null;
                filters[filter[0]].label = "";
            });

            return {
                ...state,
                filters,
                page: 1,
            };
        }
        default:
            return state;
    }
}

const Agents = (props) => {
    const isMobile = useMobile();
    const hasMounted = useHasMounted();
    const listRef = useRef(null);
    const router = useRouter();

    const { agents, agentsTotal } = props;

    const [state, dispatch] = useReducer(reducer, {
        filtersOpen: true,
        filters: props.filters,
        href: props.href,
        page: props.page,
    });

    useEffect(() => {
        if (isMobile) {
            dispatch({
                type: "hid_filters",
            });
        } else {
            dispatch({
                type: "showed_filters",
            });
        }
    }, [isMobile]);

    useEffect(() => {
        if (!hasMounted) {
            return;
        }

        onChange();
    }, [state.filters, state.page]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onChange = useCallback(
        debounce(async () => {
            let { filters, page } = state;

            const href = getHref(filters, page, true);
            const as = getHref(filters, page);

            router.replace(href, as, { shallow: false });
        }, 300),
        [state.filters, state.page]
    );

    if (props.errorCode) {
        return <Error statusCode={props.errorCode} />;
    }

    const toggleFilters = () => {
        dispatch({
            type: "toggled_filters",
        });
    };

    const handleFilterChange = (name, value) => {
        dispatch({
            type: "changed_filter",
            name,
            value,
        });
    };

    const handleClearFilter = (name, label) => {
        dispatch({
            type: "cleared_filter",
            name,
            label,
        });
    };

    const handleClearAllFilters = () => {
        dispatch({
            type: "cleared_all_filters",
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
    };

    const handlePageChange = (data) => {
        dispatch({
            type: "changed_page",
            page: data.selected + 1,
        });

        setTimeout(() => {
            listRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    };

    const { location } = state.filters;

    const pages = getPages(agentsTotal);
    const SEO = getSeoAgents(location);

    return (
        <LayoutSearch>
            <NextSeo {...SEO} />
            <div className={css.header}>
                <Breadcrumbs
                    location={location.value}
                    label={"Find an Agent"}
                />
            </div>
            <div className={css.container}>
                <div className={css.wrapper}>
                    <div className={css.filtersContainer}>
                        <div className={css.filters}>
                            <h1>
                                Search Real Estate Agents in <br />
                                {location &&
                                    location.value &&
                                    location.value.label}
                            </h1>
                            <Filters
                                values={state.filters}
                                open={state.filtersOpen}
                                onChange={handleFilterChange}
                                onSubmit={handleSubmit}
                            />

                            {isMobile ? (
                                <div className={css.layoutControl}>
                                    <div className={css.layoutControlLeft}>
                                        <a href="#" onClick={toggleFilters}>
                                            {state.filtersOpen
                                                ? "Hide"
                                                : "More"}{" "}
                                            Filters
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                ""
                            )}
                        </div>
                    </div>
                    <div className={css.agents} ref={listRef}>
                        <div className={css.agentsContainer}>
                            <ActiveFilters
                                onClear={handleClearFilter}
                                onClearAll={handleClearAllFilters}
                                filters={state.filters}
                            />
                            {agents && (
                                <>
                                    <AgentList agents={agents} />
                                    {pages > 0 && (
                                        <Paginator
                                            page={state.page}
                                            pages={pages}
                                            onPageChange={handlePageChange}
                                            href={state.href}
                                        />
                                    )}
                                    {pages === 0 && (
                                        <div style={{ marginTop: "20px" }}>
                                            Sorry, but we have no agents for the
                                            selected filters.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </LayoutSearch>
    );
};

Agents.getInitialProps = async ({ query, req, res }) => {
    let page = parseInt(query.page) || 1;
    page = getPage(page);

    let filters = null;
    let agents = null;
    let agentsTotal = 0;
    let href = null;
    let errorCode = null;

    await getFiltersFromQuery(query)
        .then((response) => {
            filters = response;
        })
        .catch(async (e) => {
            errorCode = 404;

            if (res) {
                res.statusCode = errorCode;
            }
        });

    if (filters) {
        href = getHref(filters, page);

        if (filters.location && filters.location.is_redirect) {
            let urlQuery = queryString.stringify(href.query);
            urlQuery = urlQuery ? "?" + urlQuery : "";

            // redirect old URL to valid URL
            res.writeHead(301, {
                Location: href.pathname + urlQuery,
            });

            res.end();
            return {};
        }

        // if agents failed we still can return page
        // but with hidden agents blocks
        await getAgents(filters, page)
            .then((response) => {
                agents = response.data.data;
                agentsTotal = response.data.total;

                return agents;
            })
            .catch(async (e) => {
                const queryFilters = getApiQueryFromFilters(filters);
                const status = e.response ? e.response.status : null;

                if (status !== 422) {
                    reportFailedRequest(e, req, "pages/agents.js", {
                        ...queryFilters,
                        limit: PAGE_SIZE,
                        offset: (page - 1) * PAGE_SIZE,
                    });
                }
            });
    }

    return {
        page,
        filters,
        agents,
        agentsTotal,
        href,
        errorCode,
    };
};

export default Agents;
